// Six brand-mark options for "Fresh Earth" — pure SVG, no external assets.
// Each accepts { size, tone } props. tone: 'dark' (default) or 'light'.

const palette = {
  dark:  { ink: "#1c1c1c", moss: "#4c6a58", terra: "#b86551", forest: "#1a2b22" },
  light: { ink: "#f7f6f0", moss: "#c9d9c2", terra: "#e3b09e", forest: "#f7f6f0" },
};

// ---- 1. Wordmark · current identity, refined
export const LogoWordmark = ({ size = 220, tone = "dark" }) => {
  const c = palette[tone];
  return (
    <svg width={size} height={size * 0.35} viewBox="0 0 220 76" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Fresh Earth">
      <text x="0" y="52" fontFamily="Georgia, 'Playfair Display', serif" fontStyle="italic" fontSize="44" fill={c.forest}>
        Fresh Earth
      </text>
      <line x1="0" y1="66" x2="60" y2="66" stroke={c.terra} strokeWidth="1.5" />
      <text x="68" y="70" fontFamily="Arial, Helvetica, sans-serif" fontSize="9" letterSpacing="2.6" fill={c.moss}>OED · 2019</text>
    </svg>
  );
};

// ---- 2. Leaf glyph + wordmark (biotech leaf as a spiral molecule)
export const LogoLeaf = ({ size = 220, tone = "dark" }) => {
  const c = palette[tone];
  return (
    <svg width={size} height={size * 0.4} viewBox="0 0 260 100" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Fresh Earth">
      <g transform="translate(6,10)">
        <path d="M0 40 C 12 6, 60 -6, 78 20 C 60 34, 34 46, 0 40 Z" fill={c.moss} />
        <path d="M4 40 C 22 22, 44 18, 70 22" stroke={c.forest} strokeWidth="1.2" fill="none" opacity="0.55" />
        <circle cx="46" cy="26" r="2" fill={c.terra} />
      </g>
      <text x="98" y="56" fontFamily="Georgia, serif" fontStyle="italic" fontSize="42" fill={c.forest}>Fresh Earth</text>
      <text x="98" y="76" fontFamily="Arial" fontSize="9" letterSpacing="2.6" fill={c.moss}>ODOUR ELIMINATOR &amp; DECOMPOSER</text>
    </svg>
  );
};

// ---- 3. Circular seal — great for stamps, PDFs, letterheads
export const LogoSeal = ({ size = 160, tone = "dark" }) => {
  const c = palette[tone];
  const r = 78;
  const cx = 80, cy = 80;
  return (
    <svg width={size} height={size} viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Fresh Earth">
      <defs>
        <path id="seal-arc" d={`M ${cx - r},${cy} a ${r},${r} 0 1,1 ${r * 2},0`} />
      </defs>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={c.forest} strokeWidth="1.3" />
      <circle cx={cx} cy={cy} r={r - 8} fill="none" stroke={c.forest} strokeWidth="0.6" opacity="0.4" />
      <text fontFamily="Arial" fontSize="8" letterSpacing="3.6" fill={c.forest}>
        <textPath href="#seal-arc" startOffset="8%">FRESH EARTH · SINCE 2019 · INDIA</textPath>
      </text>
      <text x={cx} y={cy + 4} fontFamily="Georgia, serif" fontStyle="italic" fontSize="24" textAnchor="middle" fill={c.forest}>Fresh</text>
      <text x={cx} y={cy + 26} fontFamily="Georgia, serif" fontStyle="italic" fontSize="24" textAnchor="middle" fill={c.forest}>Earth</text>
      <circle cx={cx} cy={cy + 40} r="2.4" fill={c.terra} />
    </svg>
  );
};

// ---- 4. Monogram FE — modern, stackable, works as favicon
export const LogoMonogram = ({ size = 120, tone = "dark" }) => {
  const c = palette[tone];
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Fresh Earth monogram">
      <rect x="4" y="4" width="112" height="112" rx="22" fill={c.forest} />
      <text x="60" y="76" fontFamily="Georgia, serif" fontSize="72" fontWeight="600" textAnchor="middle" fill={c.terra}>
        Fe
      </text>
      <text x="60" y="96" fontFamily="Arial" fontSize="7" letterSpacing="3.2" textAnchor="middle" fill={c.moss}>FRESH EARTH</text>
    </svg>
  );
};

// ---- 5. Bio-orbit — three microbial dots orbiting the wordmark
export const LogoBioOrbit = ({ size = 240, tone = "dark" }) => {
  const c = palette[tone];
  return (
    <svg width={size} height={size * 0.42} viewBox="0 0 260 110" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Fresh Earth">
      <g transform="translate(10,10)">
        <circle cx="42" cy="42" r="34" fill="none" stroke={c.moss} strokeWidth="1" opacity="0.55" />
        <circle cx="42" cy="42" r="24" fill="none" stroke={c.moss} strokeWidth="0.6" opacity="0.35" />
        <circle cx="76" cy="42" r="4" fill={c.terra} />
        <circle cx="26" cy="16" r="3" fill={c.forest} />
        <circle cx="14" cy="58" r="2.5" fill={c.moss} />
        <circle cx="42" cy="42" r="7" fill={c.forest} />
      </g>
      <text x="106" y="58" fontFamily="Georgia, serif" fontStyle="italic" fontSize="42" fill={c.forest}>Fresh Earth</text>
      <text x="106" y="80" fontFamily="Arial" fontSize="9" letterSpacing="2.6" fill={c.moss}>BIOLOGICAL · INDIGENOUS · MEASURABLE</text>
    </svg>
  );
};

// ---- 6. Horizon — flat earth-line under the wordmark (minimal, editorial)
export const LogoHorizon = ({ size = 220, tone = "dark" }) => {
  const c = palette[tone];
  return (
    <svg width={size} height={size * 0.38} viewBox="0 0 260 100" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Fresh Earth">
      <text x="0" y="52" fontFamily="Georgia, serif" fontStyle="italic" fontWeight="500" fontSize="46" fill={c.forest}>
        Fresh Earth
      </text>
      <path d="M0 72 Q 60 66 120 72 T 240 74" stroke={c.moss} strokeWidth="1.4" fill="none" />
      <circle cx="204" cy="70" r="2.5" fill={c.terra} />
      <text x="0" y="94" fontFamily="Arial" fontSize="9" letterSpacing="3" fill={c.moss}>RESTORE · REMEDIATE · RETURN</text>
    </svg>
  );
};

export const LOGO_OPTIONS = [
  { id: "wordmark",  Component: LogoWordmark,  name: "Wordmark",   note: "Refined current identity — italic serif, thin rule, product tag." },
  { id: "leaf",      Component: LogoLeaf,      name: "Leaf Mark",  note: "Biotech leaf as glyph — clear on letterheads and PDFs." },
  { id: "seal",      Component: LogoSeal,      name: "Circular Seal", note: "Stamp/emblem style — perfect for tender kits and reports." },
  { id: "monogram",  Component: LogoMonogram,  name: "Fe Monogram", note: "Compact tile — favicon, app icon, social avatar." },
  { id: "bio-orbit", Component: LogoBioOrbit,  name: "Bio-Orbit",  note: "Microbial dots orbiting the mark — cinematic on the site." },
  { id: "horizon",   Component: LogoHorizon,   name: "Horizon",    note: "Editorial minimalism — a horizon line under the wordmark." },
];
